import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { MessageSquareWarning, CheckCircle2, XCircle, Clock, Filter, ChevronDown, ChevronUp, ExternalLink } from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import { toast } from '../components/ui/sonner';
import { pingNotifications } from '../lib/notificationsBus';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_PILL = {
  pending: { Icon: Clock, cls: 'bg-amber-100 text-amber-800 border-amber-200', label: 'Pending' },
  approved: { Icon: CheckCircle2, cls: 'bg-emerald-100 text-emerald-800 border-emerald-200', label: 'Approved' },
  rejected: { Icon: XCircle, cls: 'bg-red-100 text-red-800 border-red-200', label: 'Rejected' },
  returned_for_revision: { Icon: MessageSquareWarning, cls: 'bg-orange-100 text-orange-800 border-orange-200', label: 'Needs Revision' },
};

const Pill = ({ status }) => {
  const cfg = STATUS_PILL[status] || STATUS_PILL.pending;
  const { Icon, cls, label } = cfg;
  return (
    <span data-testid={`pill-${status}`} className={`inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] font-semibold border ${cls}`} style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
      <Icon className="w-3 h-3" /> {label}
    </span>
  );
};

const fmtMoney = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

const CorrectionsInbox = () => {
  const [statusFilter, setStatusFilter] = useState('pending');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [decisionForm, setDecisionForm] = useState({}); // { [id]: { comment, commentError } }
  const [submitting, setSubmitting] = useState(null);
  const [commentsById, setCommentsById] = useState({}); // { id: [thread] }
  const [replyDrafts, setReplyDrafts] = useState({}); // { id: 'text' }
  const [returnDrafts, setReturnDrafts] = useState({}); // { id: 'text' }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = statusFilter === 'all' ? '' : `?status=${statusFilter}`;
      const res = await axios.get(`${API}/corrections/inbox${q}`, { withCredentials: true });
      setItems(res.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load corrections');
    } finally { setLoading(false); }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const setForm = (id, patch) => setDecisionForm((prev) => ({ ...prev, [id]: { ...(prev[id] || {}), ...patch } }));

  const toggleExpanded = async (item) => {
    const willOpen = !expanded[item.id];
    setExpanded((p) => ({ ...p, [item.id]: willOpen }));
    if (!willOpen) return;
    // Lazy-load comments + auto-mark-viewed (fire-and-forget)
    try {
      const r = await axios.get(`${API}/corrections/${item.id}/comments`, { withCredentials: true });
      setCommentsById((p) => ({ ...p, [item.id]: r.data || [] }));
    } catch { /* non-fatal */ }
    if (!item.first_viewed_at && item.status === 'pending') {
      try {
        await axios.post(`${API}/corrections/${item.id}/viewed`, {}, { withCredentials: true });
        pingNotifications();
        // Reflect locally so the badge appears immediately
        setItems((prev) => prev.map((x) => x.id === item.id ? { ...x, first_viewed_at: new Date().toISOString() } : x));
      } catch { /* non-fatal */ }
    }
  };

  const submitReply = async (item) => {
    const body = (replyDrafts[item.id] || '').trim();
    if (!body) return;
    try {
      await axios.post(`${API}/corrections/${item.id}/comments`, { body }, { withCredentials: true });
      const r = await axios.get(`${API}/corrections/${item.id}/comments`, { withCredentials: true });
      setCommentsById((p) => ({ ...p, [item.id]: r.data || [] }));
      setReplyDrafts((p) => ({ ...p, [item.id]: '' }));
      pingNotifications();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send reply');
    }
  };

  const submitReturn = async (item) => {
    const comment = (returnDrafts[item.id] || '').trim();
    if (!comment) { toast.error('A comment is required to return for revision.'); return; }
    setSubmitting(item.id);
    try {
      await axios.post(`${API}/corrections/${item.id}/return`, { comment }, { withCredentials: true });
      toast.success('Sent back to requester for revision');
      setReturnDrafts((p) => ({ ...p, [item.id]: '' }));
      pingNotifications();
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to return for revision');
    } finally { setSubmitting(null); }
  };

  const submit = async (item, decision) => {
    const comment = (decisionForm[item.id]?.comment || '').trim();
    if (decision === 'reject' && !comment) {
      const msg = 'A comment is required when rejecting.';
      setForm(item.id, { commentError: msg });
      toast.error(msg);
      return;
    }
    setSubmitting(item.id);
    try {
      await axios.post(`${API}/corrections/${item.id}/decide`, { decision, admin_comment: comment || null }, { withCredentials: true });
      toast.success(`Correction ${decision === 'approve' ? 'approved' : 'rejected'}`);
      pingNotifications();
      setExpanded((p) => ({ ...p, [item.id]: false }));
      setDecisionForm((p) => ({ ...p, [item.id]: undefined }));
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit decision');
    } finally { setSubmitting(null); }
  };

  return (
    <DashboardLayout>
      <div data-testid="corrections-inbox-page" className="p-4 md:p-8 max-w-6xl mx-auto">
        <div className="mb-6">
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Work Sans, sans-serif' }}>
            <MessageSquareWarning className="w-7 h-7 text-amber-600" /> Payroll Corrections
          </h1>
          <p className="text-sm text-slate-600 mt-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Review pay correction requests submitted by employees. Approve or reject with a comment that gets sent back.</p>
        </div>

        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <Filter className="w-4 h-4 text-slate-500" />
          {['pending', 'returned_for_revision', 'approved', 'rejected', 'all'].map((s) => (
            <button
              key={s}
              data-testid={s === 'returned_for_revision' ? 'filter-returned' : `filter-${s}`}
              onClick={() => setStatusFilter(s)}
              className={`px-3 py-1.5 text-xs font-semibold rounded-md border transition-all ${statusFilter === s ? 'bg-[#002FA7] text-white border-[#002FA7]' : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'}`}
              style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
            >
              {s === 'returned_for_revision' ? 'Returned' : s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>

        {loading ? (
          <p className="text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Loading...</p>
        ) : items.length === 0 ? (
          <div className="bg-white border border-dashed border-slate-300 rounded-md p-10 text-center">
            <MessageSquareWarning className="w-10 h-10 text-slate-400 mx-auto mb-2" />
            <p className="text-sm font-semibold text-slate-700" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>No {statusFilter === 'all' ? '' : statusFilter} correction requests</p>
            <p className="text-xs text-slate-500 mt-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>When an employee disputes their pay, requests will land here for review.</p>
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-md divide-y divide-slate-100">
            {items.map((item) => {
              const isOpen = !!expanded[item.id];
              const isPending = item.status === 'pending';
              const isReturned = item.status === 'returned_for_revision';
              const canDecide = isPending || isReturned;
              const thread = (commentsById[item.id] || []).filter((t) => t.kind !== 'return');
              return (
                <div key={item.id} data-testid={`correction-item-${item.id}`} className="p-4">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <p className="text-sm font-semibold text-slate-900 truncate" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{item.driver_name || 'Unknown driver'}</p>
                        <span className="text-xs text-slate-400">·</span>
                        <p className="text-xs text-slate-600">{item.week_start} → {item.week_end}</p>
                        <span className="text-xs text-slate-400">·</span>
                        <p className="text-xs text-slate-600">Gross {fmtMoney(item.entry_gross_pay)} · Net {fmtMoney(item.entry_net_pay)}</p>
                      </div>
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="inline-block px-2 py-0.5 bg-orange-100 text-orange-800 text-[10px] font-bold uppercase tracking-widest rounded">{item.category_label}</span>
                        <Pill status={item.status} />
                        {item.first_viewed_at && (
                          <span data-testid={`viewed-badge-${item.id}`} className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-50 text-emerald-700 text-[10px] font-semibold uppercase tracking-widest rounded border border-emerald-200">
                            <CheckCircle2 className="w-3 h-3" /> Viewed
                          </span>
                        )}
                        <span className="text-xs text-slate-500">Submitted {new Date(item.created_at).toLocaleString()}</span>
                      </div>
                      <p className="text-sm text-slate-700 mt-1 whitespace-pre-line line-clamp-3" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{item.description}</p>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <button data-testid={`toggle-${item.id}`} onClick={() => toggleExpanded(item)} className="text-xs font-semibold text-slate-600 hover:text-slate-900 inline-flex items-center gap-1">
                        {isOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                        {canDecide ? 'Review' : 'Details'}
                      </button>
                      <Link to={`/payroll/${item.payroll_id}`} data-testid={`open-payroll-${item.id}`} className="text-xs text-[#002FA7] font-semibold hover:underline inline-flex items-center gap-1">
                        <ExternalLink className="w-3 h-3" /> Open Payroll
                      </Link>
                    </div>
                  </div>

                  {isOpen && (
                    <div className="mt-3 pt-3 border-t border-slate-100 space-y-3">
                      {isReturned && item.admin_comment && (
                        <div data-testid={`returned-banner-${item.id}`} className="p-2 border border-orange-200 bg-orange-50 rounded text-sm text-orange-900">
                          <p className="text-[11px] font-semibold uppercase tracking-widest mb-1">You returned this for revision</p>
                          {item.admin_comment}
                        </div>
                      )}
                      {(item.status === 'approved' || item.status === 'rejected') && item.admin_comment && (
                        <div className={`p-2 border rounded text-sm ${item.status === 'approved' ? 'bg-emerald-50 border-emerald-200 text-emerald-900' : 'bg-red-50 border-red-200 text-red-900'}`}>
                          <p className="text-[11px] font-semibold uppercase tracking-widest mb-1">Your reply{item.decided_by_name ? ` · ${item.decided_by_name}` : ''}{item.decided_at ? ` · ${new Date(item.decided_at).toLocaleString()}` : ''}</p>
                          {item.admin_comment}
                        </div>
                      )}

                      {/* Threaded conversation */}
                      {thread.length > 0 && (
                        <div data-testid={`thread-${item.id}`} className="space-y-2">
                          {thread.map((t) => (
                            <div key={t.id} data-testid={`comment-${t.id}`} className={`p-2 rounded text-sm ${t.author_role === 'driver' || t.author_role === 'dispatcher' ? 'bg-slate-50 border border-slate-200' : 'bg-blue-50 border border-blue-200'}`}>
                              <p className="text-[11px] font-semibold uppercase tracking-widest mb-1 text-slate-700">
                                {t.author_name} <span className="font-normal text-slate-500">· {t.author_role.replace('_', ' ')} · {new Date(t.created_at).toLocaleString()}</span>
                              </p>
                              <p className="text-slate-800 whitespace-pre-line">{t.body}</p>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Quick reply (any status except terminal) */}
                      {canDecide && (
                        <div className="border-t border-slate-100 pt-3 space-y-2">
                          <textarea
                            data-testid={`reply-input-${item.id}`}
                            value={replyDrafts[item.id] || ''}
                            onChange={(e) => setReplyDrafts((p) => ({ ...p, [item.id]: e.target.value }))}
                            rows={2}
                            placeholder="Reply to the requester…"
                            className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
                          />
                          <button
                            data-testid={`send-reply-${item.id}`}
                            onClick={() => submitReply(item)}
                            disabled={!(replyDrafts[item.id] || '').trim()}
                            className="bg-[#002FA7] text-white px-3 py-1.5 rounded-md text-xs font-semibold hover:bg-[#00247D] disabled:opacity-50"
                          >Send Reply</button>
                        </div>
                      )}

                      {canDecide && (
                        <div className="border-t border-slate-100 pt-3 space-y-2">
                          <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Decision comment (required for rejection)</label>
                          <textarea
                            data-testid={`comment-${item.id}`}
                            value={decisionForm[item.id]?.comment || ''}
                            onChange={(e) => setForm(item.id, { comment: e.target.value, commentError: '' })}
                            rows={3}
                            placeholder="e.g. Added the missing load. Will re-run payroll today."
                            className={`w-full px-3 py-2 border rounded-md text-sm ${decisionForm[item.id]?.commentError ? 'border-red-400 ring-1 ring-red-200' : 'border-slate-300'}`}
                            style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                          />
                          {decisionForm[item.id]?.commentError && (
                            <p data-testid={`comment-error-${item.id}`} className="text-xs text-red-600 -mt-1">{decisionForm[item.id].commentError}</p>
                          )}
                          <div className="flex items-center gap-2 flex-wrap">
                            <button data-testid={`approve-${item.id}`} disabled={submitting === item.id} onClick={() => submit(item, 'approve')} className="flex items-center gap-1.5 bg-emerald-600 text-white px-3 py-2 rounded-md text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50">
                              <CheckCircle2 className="w-4 h-4" /> Approve
                            </button>
                            <button data-testid={`reject-${item.id}`} disabled={submitting === item.id} onClick={() => submit(item, 'reject')} className="flex items-center gap-1.5 bg-red-600 text-white px-3 py-2 rounded-md text-sm font-semibold hover:bg-red-700 disabled:opacity-50">
                              <XCircle className="w-4 h-4" /> Reject
                            </button>
                            {isPending && (
                              <div className="flex-1 min-w-[280px]">
                                <div className="flex items-stretch gap-2">
                                  <input
                                    data-testid={`return-comment-${item.id}`}
                                    type="text"
                                    placeholder="What more info do you need? (required to return)"
                                    value={returnDrafts[item.id] || ''}
                                    onChange={(e) => setReturnDrafts((p) => ({ ...p, [item.id]: e.target.value }))}
                                    className="flex-1 px-3 py-2 border border-orange-300 rounded-md text-sm"
                                  />
                                  <button
                                    data-testid={`return-${item.id}`}
                                    disabled={submitting === item.id || !(returnDrafts[item.id] || '').trim()}
                                    onClick={() => submitReturn(item)}
                                    className="flex items-center gap-1.5 bg-orange-500 text-white px-3 py-2 rounded-md text-sm font-semibold hover:bg-orange-600 disabled:opacity-50"
                                  >
                                    <MessageSquareWarning className="w-4 h-4" /> Return
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default CorrectionsInbox;
